import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../utils/api';

// Create the authentication context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Check if user is already logged in (has valid token)
  useEffect(() => {
    const checkAuthStatus = async () => {
      // Skip the auth check if we're on the login page
      if (window.location.pathname.includes('/login')) {
        setCurrentUser(null);
        setLoading(false);
        return;
      }
      
      try {
        const data = await authAPI.checkAuth();
        setCurrentUser(data.user);
      } catch (err) {
        console.error('Auth check failed:', err);
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };

    // Check for auth errors from interceptors
    const handleStorageChange = () => {
      const authError = localStorage.getItem('auth_error');
      if (authError === 'true') {
        // Clear the flag
        localStorage.removeItem('auth_error');
        // Log the user out locally
        setCurrentUser(null);
      }
    };

    // Listen for storage events (for cross-tab communication)
    window.addEventListener('storage', handleStorageChange);
    // Also check immediately
    handleStorageChange();

    checkAuthStatus();

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Login function
  const login = (user) => {
    setCurrentUser(user);
    setError('');
  };

  // Logout function
  const logout = async () => {
    try {
      await authAPI.logout();
      setCurrentUser(null);
    } catch (err) {
      console.error('Logout failed:', err);
      setError('Logout failed. Please try again.');
    }
  };

  // Context value
  const value = {
    currentUser,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!currentUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
